import { useState, useEffect } from 'react';
import { adminApi, type WithdrawRecord } from '@/lib/admin';
import { useToast } from '@/components/base/Toast';
import { SectionCard, StatusBadge, EmptyState, LoadingState, ErrorState, ConfirmDialog } from '@/pages/admin/components/UI';

const statusMaps = {
  pending: { label: '待审核', className: 'bg-gold-100 text-gold-700', icon: 'ri-time-line' },
  approved: { label: '已通过', className: 'bg-green-100 text-green-600', icon: 'ri-check-line' },
  rejected: { label: '已驳回', className: 'bg-red-100 text-red-500', icon: 'ri-close-line' },
  default: { label: '未知', className: 'bg-brown-100 text-brown-600', icon: 'ri-question-line' },
};

const tabs = [
  { key: 'all', label: '全部' },
  { key: 'pending', label: '待审核' },
  { key: 'approved', label: '已通过' },
  { key: 'rejected', label: '已驳回' },
];

export default function AdminWithdrawals() {
  const { showToast } = useToast();
  const [records, setRecords] = useState<WithdrawRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [tab, setTab] = useState('all');
  const [actionTarget, setActionTarget] = useState<WithdrawRecord | null>(null);
  const [actionType, setActionType] = useState<'approve' | 'reject'>('approve');
  const [busy, setBusy] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    setError(false);
    try {
      const data = await adminApi.listWithdrawals();
      setRecords(data);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleAction = async () => {
    if (!actionTarget) return;
    setBusy(true);
    try {
      if (actionType === 'approve') {
        await adminApi.approveWithdrawal(actionTarget.id);
        showToast('已通过该出金申请', 'success');
      } else {
        await adminApi.rejectWithdrawal(actionTarget.id);
        showToast('已驳回该出金申请', 'success');
      }
      setActionTarget(null);
      await fetchData();
    } catch (e) {
      showToast(e instanceof Error ? e.message : '操作失败', 'error');
    } finally {
      setBusy(false);
    }
  };

  const filtered = tab === 'all' ? records : records.filter((r) => r.status === tab);
  const pendingTotal = records.filter((r) => r.status === 'pending').length;

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-brown-900 text-lg font-bold">出金管理</h2>
          <p className="text-brown-400 text-xs mt-0.5">
            {records.length > 0 ? `共 ${records.length} 笔 · 待审核 ${pendingTotal} 笔` : '暂无出金记录'}
          </p>
        </div>
        <div className="inline-flex bg-white rounded-lg border border-brown-100 p-1 self-start">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium cursor-pointer whitespace-nowrap transition-colors ${
                tab === t.key ? 'bg-gold-500 text-brown-950' : 'text-brown-500 hover:text-brown-700'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <SectionCard>
        {loading ? (
          <LoadingState />
        ) : error ? (
          <ErrorState onRetry={fetchData} />
        ) : filtered.length === 0 ? (
          <EmptyState title="暂无出金记录" desc="用户提交提现后，待审核的出金会出现在这里" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-brown-400 text-xs border-b border-brown-100">
                  <th className="px-4 md:px-5 py-3 font-medium">用户</th>
                  <th className="px-4 py-3 font-medium">提现金额</th>
                  <th className="px-4 py-3 font-medium hidden sm:table-cell">到账方式</th>
                  <th className="px-4 py-3 font-medium hidden md:table-cell">申请时间</th>
                  <th className="px-4 py-3 font-medium">状态</th>
                  <th className="px-4 py-3 font-medium text-right">操作</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id} className="border-b border-brown-50 last:border-0 hover:bg-brown-50/40 transition-colors">
                    <td className="px-4 md:px-5 py-3.5">
                      <p className="text-brown-900 font-medium">{r.phone || '—'}</p>
                      <p className="text-brown-400 text-xs">#{r.id}</p>
                    </td>
                    <td className="px-4 py-3.5">
                      <p className="text-brown-900 font-semibold">
                        ¥{Number(r.amount || 0).toLocaleString('zh-CN', { minimumFractionDigits: 2 })}
                      </p>
                    </td>
                    <td className="px-4 py-3.5 hidden sm:table-cell text-brown-700">{r.method || '—'}</td>
                    <td className="px-4 py-3.5 hidden md:table-cell text-brown-400 text-xs">{r.create_time || '—'}</td>
                    <td className="px-4 py-3.5">
                      <StatusBadge status={r.status} maps={statusMaps} />
                    </td>
                    <td className="px-4 py-3.5 text-right whitespace-nowrap">
                      {r.status === 'pending' ? (
                        <div className="inline-flex items-center gap-2">
                          <button
                            onClick={() => {
                              setActionType('approve');
                              setActionTarget(r);
                            }}
                            className="px-3 py-1.5 rounded-md bg-green-500 text-white text-xs font-medium cursor-pointer whitespace-nowrap hover:bg-green-600 transition-colors"
                          >
                            通过
                          </button>
                          <button
                            onClick={() => {
                              setActionType('reject');
                              setActionTarget(r);
                            }}
                            className="px-3 py-1.5 rounded-md border border-red-100 text-red-500 text-xs cursor-pointer whitespace-nowrap hover:bg-red-50 transition-colors"
                          >
                            驳回
                          </button>
                        </div>
                      ) : (
                        <span className="text-brown-300 text-xs">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      <ConfirmDialog
        open={!!actionTarget}
        title={actionType === 'approve' ? '通过该出金申请？' : '驳回该出金申请？'}
        desc={
          actionType === 'approve'
            ? '通过后将标记为已通过，并从该用户账户扣减对应金额。'
            : '驳回后该申请将标记为已驳回。'
        }
        confirmText={actionType === 'approve' ? '确认通过' : '确认驳回'}
        danger={actionType === 'reject'}
        loading={busy}
        onCancel={() => setActionTarget(null)}
        onConfirm={handleAction}
      />
    </div>
  );
}